<template>
  <div v-if="currency" class="input-group">
    <input
        v-bind="attributes"
        ref="input"
        @focus="onFocus"
        @input="onInput"
        @blur="onBlur"
        type="text"
        inputmode="decimal"
    />
    <div class="input-group-append">
      <div class="input-group-text"><span class="ruble">&#8381;</span></div>
    </div>
  </div>

  <input
      v-else
      v-bind="attributes"
      ref="input"
      @focus="onFocus"
      @input="onInput"
      @blur="onBlur"
      type="text"
      inputmode="decimal"
  />
</template>

<script>
import {formatAmount} from "@/components/common";

export default {
  name: "InputMoney",
  inheritAttrs: false,
  props: {
    modelValue: null,
    min: null,
    max: null,
    allowFractions: { type: Boolean, default: false },
    thousands: { type: Boolean, default: true },
    thousandsDelimiter: { type: String, default: " " },
    currency: { type: Boolean, default: false },
    fractionDigits: { type: Number, default: 2 }
  },
  data() {
    return {
      isFocused: false
    };
  },
  computed: {
    attributes() {
      return Object.keys(this.$attrs).reduce(
          (prev, key) => ({ ...prev, [key]: this.$attrs[key] }),
          {}
      );
    }
  },

  mounted() {
    this.applyFromModel(this.modelValue);
  },

  watch: {
    modelValue(newVal) {
      if (this.isFocused) return;
      this.applyFromModel(newVal);
    }
  },

  methods: {
    onFocus() {
      this.isFocused = true;
    },

    onInput(e) {
      const raw = e?.target?.value ?? "";
      this.updateValue(raw, false);
    },

    onBlur(e) {
      this.isFocused = false;
      const raw = e?.target?.value ?? "";
      this.updateValue(raw, true);
    },

    applyFromModel(value) {
      if (!this.$refs.input) return;
      if (value === null || value === undefined || value === "") {
        this.$refs.input.value = "";
        return;
      }

      if (this.allowFractions) {
        this.$refs.input.value = String(value).replace(/\./g, ",");
        return;
      }
      const amount = Number(value);
      if (!Number.isFinite(amount)) return;

      this.$refs.input.value = formatAmount(amount, {
        thousands: this.thousands,
        thousandsDelimiter: this.thousandsDelimiter
      });
    },

    updateValue(value, withLimits) {
      if (!this.$refs.input) return;
      if (this.allowFractions) {
        let s = (value ?? "").toString();
        s = s.replace(/[，٬٫·]/g, ".");
        s = s.replace(/,/g, ".");
        s = s.replace(/[^0-9.]/g, "");
        const firstDot = s.indexOf(".");
        if (firstDot !== -1) {
          s =
              s.slice(0, firstDot + 1) +
              s.slice(firstDot + 1).replace(/\./g, "");
        }
        const [intPart, fracPart = ""] = s.split(".");
        const trimmedFrac = fracPart.slice(0, this.fractionDigits);
        const nextDisplay =
            firstDot === -1 ? intPart : `${intPart},${trimmedFrac}`;
        this.$refs.input.value = nextDisplay;
        this.$emit("update:modelValue", nextDisplay);
        if (withLimits && nextDisplay !== "" && nextDisplay !== "," && nextDisplay !== ".") {
          const num = Number(String(nextDisplay).replace(",", "."));
          if (Number.isFinite(num)) {
            let clamped = num;

            const min = this.min !== null ? Number(this.min) : null;
            const max = this.max !== null ? Number(this.max) : null;

            if (min !== null && Number.isFinite(min) && clamped < min) clamped = min;
            if (max !== null && Number.isFinite(max) && clamped > max) clamped = max;
            const fixed = clamped.toFixed(this.fractionDigits).replace(".", ",");
            this.$refs.input.value = fixed;
            this.$emit("update:modelValue", fixed);
          }
        }

        return;
      }
      let amount;
      let sanitizedValue =
          value || value === 0 ? value.toString().replace(/\D+/g, "") : "";

      if (sanitizedValue.length > 0) {
        if (sanitizedValue.length > 15) {
          sanitizedValue = sanitizedValue.substr(0, 15);
        }

        const parsedAmount = parseInt(sanitizedValue, 10);
        if (!isNaN(parsedAmount)) {
          amount = parsedAmount;

          if (withLimits) {
            const min = +this.min;
            if (this.min !== null && !isNaN(min) && amount < min) amount = min;

            const max = +this.max;
            if (this.max !== null && !isNaN(max) && amount > max) amount = max;
          }
        }
      }

      if (amount !== undefined) {
        this.$refs.input.value = formatAmount(amount, {
          thousands: this.thousands,
          thousandsDelimiter: this.thousandsDelimiter
        });
        this.$emit("update:modelValue", amount);
      }
    }
  }
};
</script>